import { ARTICLE_TOKENS } from '../design-tokens';
import { buildSanityImageSrcSet, buildSanityImageUrl } from '@lib/sanity/image';

const BODY_IMAGE_MAX_WIDTH = 1200;
const BODY_IMAGE_QUALITY = 80;
// The article column is 700px wide, so 1200 covers a 2x display; the smaller
// breakpoints stop phones downloading the desktop variant.
const BODY_IMAGE_WIDTHS = [480, 720, 960, BODY_IMAGE_MAX_WIDTH];
const BODY_IMAGE_SIZES = '(min-width: 48rem) 44rem, 100vw';
// A tall graphic fits on one phone screen with room left for its caption.
// Small viewport units ignore the browser's collapsing toolbars.
const BODY_IMAGE_MAX_HEIGHT = '75svh';

/** The body image as the article query expands it, asset metadata included. */
export type SanityBodyImage = {
  _type: 'image';
  _key?: string;
  asset?: {
    _ref?: string;
    _id?: string;
    url?: string;
    metadata?: { dimensions?: { width?: number; height?: number } };
  };
  alt?: string;
  caption?: string;
  credit?: string;
};

/**
 * A body image with its caption and credit beneath, in the same style as the
 * featured image so a picture credit reads the same wherever it sits.
 */
export const BodyImage = ({ value }: { value: SanityBodyImage }) => {
  if (!value?.asset) return null;
  // fit('max') resizes down only, so smaller uploads are untouched.
  const imageUrl = buildSanityImageUrl(value)
    .width(BODY_IMAGE_MAX_WIDTH)
    .fit('max')
    .quality(BODY_IMAGE_QUALITY)
    .url();
  const { width, height } = value.asset?.metadata?.dimensions ?? {};
  const hasCaption = Boolean(value.caption || value.credit);

  const image = (
    <img
      src={imageUrl}
      srcSet={buildSanityImageSrcSet(
        value,
        BODY_IMAGE_WIDTHS,
        BODY_IMAGE_QUALITY
      )}
      sizes={BODY_IMAGE_SIZES}
      alt={value.alt ?? ''}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      style={{
        maxWidth: '100%',
        maxHeight: BODY_IMAGE_MAX_HEIGHT,
        // A capped height no longer matches the image's shape; contain scales
        // the picture inside the box instead of squashing it.
        objectFit: 'contain',
        height: 'auto',
        display: 'block',
        margin: '0 auto'
      }}
    />
  );

  if (!hasCaption) return image;

  return (
    <figure className="not-prose my-7" data-cy="body-image">
      {image}
      <figcaption
        className={`mt-2.5 text-center text-[13px] ${ARTICLE_TOKENS.mutedLight}`}
      >
        {value.caption}
        {value.credit && (
          <span className={ARTICLE_TOKENS.mutedLighter}>
            {value.caption ? ' · ' : ''}
            {value.credit}
          </span>
        )}
      </figcaption>
    </figure>
  );
};
